import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { Link as DomLink } from "react-router-dom";
import * as Yup from "yup";
import { getAllImagesAndUrl } from "../../../pages/BookingDashboard/apis/BrandingModalApi";
import {
  getHotel,
  login,
  masterLoginV2,
  masterLoginV2Otp,
  hotelLoginV2,
} from "../_redux/authCrud";
import * as auth from "../_redux/authRedux";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
import { formatDisplayTimeForOtpEpoch } from "./utils/allowedEmails";
import { getAuthErrorMessage, isRateLimitMessage, parseAuthApiErrorMessage } from "./utils/parseAuthApiError";

const EmailSuccess = (props) => {
  const { t } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(props.selectedLanguage);
  }, [props.selectedLanguage]);

  const LoginSchema = Yup.object().shape({
    passw: Yup.string().required(`${t("Password is required")}`),
  });
  const MasterLoginSchema = Yup.object().shape({
    username: Yup.string().required(`${t("master email is required")}`),
    passw: Yup.string().required(`${t("master password is required")}`),
  });
  const masterAdminCheck =
    window.location.pathname.split("/")[1] === "masterAdmin" ? true : false;

  const { loginTrue, mailId, isV2AuthEnabled } = props;
  const [loading, setLoading] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [invalidLogin, setInvalidLogin] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpId, setOtpId] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(0);
  const [timerTick, setTimerTick] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [otpInvalid, setOtpInvalid] = useState(false);

  // Master login always uses v2 OTP (same as non-whitelabel). Hotel login uses v2 when enabled.
  const useV2AuthFlow = masterAdminCheck || Boolean(isV2AuthEnabled);

  const formik = useFormik({
    initialValues: { passw: "" },
    validationSchema: LoginSchema,
    onSubmit: async (values) => {
      validUser(values);
    },
  });

  const masterFormik = useFormik({
    initialValues: { username: "", passw: "" },
    validationSchema: MasterLoginSchema,
    onSubmit: async (values) => {
      validMasterUser(values);
    },
  });

  const completeLogin = async (resp) => {
    const hotelData = await getHotel(
      mailId,
      resp.groupProperty,
      `${resp.token}`
    );
    if (!hotelData?.data?.length) {
      throw new Error("No hotels found for this account");
    }
    const temp = Object.assign(resp);
    temp.hotels = hotelData.data;
    temp.email = mailId;
    temp.token = `${resp.token}`;
    localStorage.setItem("hotelId", hotelData.data[0].hotelId);
    localStorage.setItem("token", `${resp.token}`);
    if (masterAdminCheck && masterFormik.values.username) {
      localStorage.setItem("rootEmail", masterFormik.values.username);
    }
    props.login(temp);
    localStorage.setItem("HotelEmail", mailId);
  };

  const validMasterUser = async (values) => {
    setLoading(true);
    setInvalid(false);
    setAuthErrorMessage("");
    try {
      // Whitelabel master login uses v2 OTP flow (same as NewEmailSuccessPage1)
      const temporary = await masterLoginV2(
        mailId,
        values.username,
        values.passw
      );
      const resp = temporary.data;

      if (!resp?.otpid || (resp?.status != null && Number(resp.status) !== 200)) {
        setInvalid(true);
        setAuthErrorMessage(
          resp?.message || t("invalid master credentials")
        );
        setLoading(false);
        return;
      }

      setOtpId(resp?.otpid);
      setOtpExpiresAt(resp?.otpExpiresAt || 0);
      setOtpVerified(true);
      setOtpAttempts(0);
      setOtpInvalid(false);
      setInvalid(false);
      setLoading(false);
    } catch (e) {
      setInvalid(true);
      setAuthErrorMessage(
        getAuthErrorMessage(e, {
          fallback: t("Something went wrong, couldn't send the OTP"),
          rateLimitFallback: t(
            "Too many attempts. Please wait 15 minutes and try again."
          ),
        })
      );
      setLoading(false);
    }
  };

  const handleOtpSignIn = async () => {
    if (!otpValue.trim()) {
      setOtpInvalid(true);
      return;
    }
    setOtpLoading(true);
    setOtpInvalid(false);
    setAuthErrorMessage("");
    try {
      const otpResponse = await masterLoginV2Otp(otpId, otpValue);
      const resp = otpResponse.data;

      if (!resp?.token || (resp?.status != null && Number(resp.status) !== 200)) {
        const bodyMessage = resp?.message || t("Invalid OTP");
        if (isRateLimitMessage(resp?.status, bodyMessage)) {
          setOtpLoading(false);
          setOtpInvalid(true);
          setAuthErrorMessage(
            bodyMessage ||
              t("Too many attempts. Please wait 15 minutes and try again.")
          );
          return;
        }
        throw new Error(bodyMessage);
      }

      await completeLogin(resp);
      setOtpLoading(false);
    } catch (e) {
      const { message, status } = parseAuthApiErrorMessage(e);
      const serverMessage = getAuthErrorMessage(e, {
        fallback: t("Invalid OTP"),
        rateLimitFallback: t(
          "Too many attempts. Please wait 15 minutes and try again."
        ),
      });

      // Rate-limit: show message, keep OTP form, do not burn attempts
      if (isRateLimitMessage(status, message || serverMessage)) {
        setOtpLoading(false);
        setOtpInvalid(true);
        setOtpValue("");
        setAuthErrorMessage(serverMessage);
        return;
      }

      const newAttempts = otpAttempts + 1;
      setOtpAttempts(newAttempts);
      setOtpLoading(false);
      setOtpInvalid(true);
      setOtpValue("");

      if (newAttempts >= 3) {
        setOtpVerified(false);
        setOtpId("");
        setOtpExpiresAt(0);
        setOtpValue("");
        setOtpAttempts(0);
        setOtpInvalid(false);
        setInvalid(true);
        setAuthErrorMessage(
          t("Maximum OTP attempts reached. Please start over.")
        );
      } else {
        setAuthErrorMessage(serverMessage);
      }
    }
  };

  const validUser = async (values) => {
    setLoading(true);
    setInvalid(false);
    setInvalidLogin(false);
    setAuthErrorMessage("");
    try {
      if (isV2AuthEnabled) {
        const temporary = await hotelLoginV2(mailId, values.passw);
        const resp = temporary.data;

        if (!resp?.otpid || (resp?.status != null && Number(resp.status) !== 200)) {
          setInvalid(true);
          setAuthErrorMessage(
            resp?.message ||
              t("Login failed. Please check your credentials.")
          );
          setLoading(false);
          return;
        }

        setOtpId(resp?.otpid);
        setOtpExpiresAt(resp?.otpExpiresAt || 0);
        setOtpVerified(true);
        setOtpAttempts(0);
        setOtpInvalid(false);
        setInvalid(false);
        setLoading(false);
      } else {
        const temporary = await login(mailId, values.passw);
        localStorage.setItem("HotelEmail", mailId);
        if (temporary.data.token === "Invalid login hours") {
          setInvalidLogin(true);
          setLoading(false);
        } else if (temporary.data.token === "error" || !temporary.data.token) {
          setInvalid(true);
          setAuthErrorMessage(
            temporary.data.message ||
              t(
                "You have exceeded the maximum login attempts. Please wait 15 minutes and try again."
              )
          );
          setLoading(false);
        } else {
          await completeLogin(temporary.data);
          setLoading(false);
        }
      }
    } catch (e) {
      setInvalid(true);
      setAuthErrorMessage(
        getAuthErrorMessage(e, {
          fallback: isV2AuthEnabled
            ? t("Something went wrong, couldn't send the OTP")
            : t("Please enter correct password"),
          rateLimitFallback: t(
            "You have exceeded the maximum login attempts. Please wait 15 minutes and try again."
          ),
        })
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!otpVerified || otpExpiresAt <= 0) return;

    const interval = setInterval(() => {
      const remaining = otpExpiresAt - Math.floor(Date.now() / 1000);
      if (remaining <= 0) {
        setOtpVerified(false);
        setOtpId("");
        setOtpExpiresAt(0);
        setOtpValue("");
        setOtpAttempts(0);
        setOtpInvalid(false);
        setInvalid(true);
        setAuthErrorMessage(t("OTP expired. Please request a new one."));
      } else {
        setTimerTick((prev) => prev + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [otpVerified, otpExpiresAt]);

  const [showHelper, setshowHelper] = useState("");
  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = async () => {
    try {
      const data = await getAllImagesAndUrl();
      setshowHelper(data.status);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <form
      noValidate
      onSubmit={
        masterAdminCheck ? masterFormik.handleSubmit : formik.handleSubmit
      }
    >
      <Grid container display="grid" spacing={4}>
        <Grid item xs={12}>
          <Typography variant="h3" paddingBottom={1}>
            {masterAdminCheck ? `${t("Master login")}` : `${t("Welcome back")}`}
          </Typography>
          <Typography variant="body1">{mailId}</Typography>
        </Grid>
        {masterAdminCheck && (
          <Grid item xs={12} spacing={10}>
            <TextField
              value={masterFormik.values.username}
              onChange={(e) => {
                masterFormik.setFieldValue("username", e.target.value);
                if (invalid) setInvalid(false);
                if (authErrorMessage) setAuthErrorMessage("");
              }}
              fullWidth
              type={"text"}
              onBlur={masterFormik.handleBlur}
              name="username"
              id="username"
              sx={{ marginBottom: "1rem" }}
              autoFocus
              error={
                (masterFormik.touched.username &&
                  Boolean(masterFormik.errors.username)) ||
                invalid ||
                Boolean(!otpVerified && authErrorMessage)
              }
              helperText={
                (masterFormik.touched.username &&
                  masterFormik.errors.username) ||
                (!otpVerified && authErrorMessage) ||
                (invalid && t("Invalid master email"))
              }
              label={`${t("Master email")}`}
            ></TextField>
            <TextField
              value={masterFormik.values.passw}
              onChange={(e) => {
                masterFormik.setFieldValue("passw", e.target.value);
                if (invalid) setInvalid(false);
                if (authErrorMessage) setAuthErrorMessage("");
              }}
              fullWidth
              type={showPassword ? "text" : "password"}
              onBlur={masterFormik.handleBlur}
              name="passw"
              id="passw"
              error={
                (masterFormik.touched.passw &&
                  Boolean(masterFormik.errors.passw)) ||
                invalid ||
                Boolean(!otpVerified && authErrorMessage)
              }
              helperText={
                (masterFormik.touched.passw && masterFormik.errors.passw) ||
                (!otpVerified && authErrorMessage) ||
                (invalid && `${t("Invalid master password")}`)
              }
              label={`${t("Master password")}`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            ></TextField>
          </Grid>
        )}
        {!masterAdminCheck && (
          <Grid item xs={12}>
            <TextField
              value={formik.values.passw}
              onChange={(e) => {
                formik.setFieldValue("passw", e.target.value);
                if (invalid) setInvalid(false);
                if (invalidLogin) setInvalidLogin(false);
                if (authErrorMessage) setAuthErrorMessage("");
              }}
              fullWidth
              type={showPassword ? "text" : "password"}
              onBlur={formik.handleBlur}
              name="passw"
              id="passw"
              autoFocus
              error={
                (formik.touched.passw && Boolean(formik.errors.passw)) ||
                invalid ||
                invalidLogin ||
                Boolean(!otpVerified && authErrorMessage)
              }
              helperText={
                (formik.touched.passw && formik.errors.passw) ||
                (!otpVerified && authErrorMessage) ||
                (invalid && `${t("Invalid password")}`) ||
                (invalidLogin && `${t("Invalid login hours")}`)
              }
              label={`${t("Password")}`}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            ></TextField>
          </Grid>
        )}
        {otpVerified && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="otp"
                id="otp"
                value={otpValue}
                onChange={(e) => {
                  setOtpValue(e.target.value);
                  if (otpInvalid) setOtpInvalid(false);
                  if (authErrorMessage) setAuthErrorMessage("");
                }}
                error={otpInvalid || Boolean(authErrorMessage && otpVerified)}
                helperText={
                  authErrorMessage && otpVerified
                    ? authErrorMessage
                    : otpInvalid
                    ? otpAttempts >= 3
                      ? t("Too many failed attempts. Please try again.")
                      : t(
                          `Invalid OTP. ${3 - otpAttempts} attempts remaining.`
                        )
                    : ""
                }
                label={`${t("Enter OTP")}`}
                autoFocus
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                color={
                  formatDisplayTimeForOtpEpoch(otpExpiresAt) === "0:00"
                    ? "error"
                    : "textSecondary"
                }
                align="center"
              >
                {timerTick >= 0 && formatDisplayTimeForOtpEpoch(otpExpiresAt)}
              </Typography>
            </Grid>
          </>
        )}
        {showHelper !== "ENABLED" && !masterAdminCheck && (
          <Grid item xs={12}>
            <Link
              underline="hover"
              component={DomLink}
              to={{
                pathname: masterAdminCheck
                  ? "/masterAdmin/forget-password"
                  : "/auth/forget-password",
                state: {
                  email: mailId,
                },
              }}
            >
              {`${t("Forgot your password")}?`}
            </Link>
          </Grid>
        )}
        {window.location.hostname === "pms.isha.in" && (
          <Grid item xs={12}>
            <Link
              underline="hover"
              component={DomLink}
              to={{
                pathname: masterAdminCheck
                  ? "/masterAdmin/forget-password"
                  : "/auth/forget-password",
                state: {
                  email: mailId,
                },
              }}
            >
              {" "}
              {`${t("Forgot your password")}?`}
            </Link>
          </Grid>
        )}
        <Grid item xs={12}>
          <LoadingButton
            sx={{ height: "150%" }}
            loading={otpVerified ? otpLoading : loading}
            disabled={(otpVerified && otpLoading) || (!otpVerified && loading)}
            type={otpVerified ? "button" : "submit"}
            onClick={otpVerified ? handleOtpSignIn : undefined}
            fullWidth
            variant="custom-button"
            size="large"
          >
            {useV2AuthFlow && otpVerified
              ? `${t("Sign In")}`
              : useV2AuthFlow
              ? `${t("Verify with Email OTP")}`
              : `${t("Sign In")}`}
          </LoadingButton>
        </Grid>
        <Grid item container justifyContent="center" xs={12}>
          <Link onClick={loginTrue} underline="hover" to="/">
            {`${t("Back")}`}
          </Link>
        </Grid>
      </Grid>
    </form>
  );
};

export default injectIntl(connect(null, auth.actions)(EmailSuccess));
